import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import { extractEdgeFunctionError } from '../utils/edgeFunctionUtils';

/**
 * Company Service - Handles multi-company operations and company management
 */

// Get all companies (for Super Admin dashboard)
export const getAllCompanies = async () => {
  try {
    const { data, error } = await supabase?.from('companies')?.select(`
        id,
        company_name,
        company_tag,
        status,
        created_at,
        updated_at,
        primary_owner:user_profiles!companies_primary_owner_id_fkey (
          id,
          full_name,
          email,
          username
        )
      `)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Get user and job counts for each company, and fallback owner if needed
    const companiesWithStats = await Promise.all(
      (data || [])?.map(async (company) => {
        const [usersCount, jobsCount] = await Promise.all([
          getCompanyUsersCount(company?.id),
          getCompanyJobsCount(company?.id)
        ]);

        // If primary_owner is null, fetch from company_users as fallback
        let primaryOwner = null;
        if (company?.primary_owner) {
          primaryOwner = {
            id: company?.primary_owner?.id,
            fullName: company?.primary_owner?.full_name,
            email: company?.primary_owner?.email,
            username: company?.primary_owner?.username
          };
        } else {
          // Fallback: get primary_owner from company_users table
          const { data: ownerData } = await supabase
            ?.from('company_users')
            ?.select(`
              user:user_profiles (
                id,
                full_name,
                email,
                username
              )
            `)
            ?.eq('company_id', company?.id)
            ?.eq('role', 'primary_owner')
            ?.eq('is_active', true)
            ?.single();

          if (ownerData?.user) {
            primaryOwner = {
              id: ownerData?.user?.id,
              fullName: ownerData?.user?.full_name,
              email: ownerData?.user?.email,
              username: ownerData?.user?.username
            };
          }
        }

        return {
          id: company?.id,
          companyName: company?.company_name,
          companyTag: company?.company_tag,
          status: company?.status,
          createdAt: company?.created_at,
          updatedAt: company?.updated_at,
          primaryOwner,
          userCount: usersCount,
          jobCount: jobsCount
        };
      })
    );

    return {
      success: true,
      data: companiesWithStats
    };
  } catch (error) {
    console.error('Error fetching all companies:', error);
    return { success: false, error: error?.message };
  }
};

// Get all companies user is part of
export const getUserCompanies = async (userId) => {
  try {
    const { data, error } = await supabase?.from('company_users')?.select(`
        id,
        role,
        is_active,
        company_alias,
        company:companies (
          id,
          company_name,
          company_tag,
          status,
          created_at
        )
      `)?.eq('user_id', userId)?.eq('is_active', true)?.order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data?.map(item => ({
        membershipId: item?.id,
        role: item?.role,
        companyAlias: item?.company_alias,
        ...item?.company
      })) || []
    };
  } catch (error) {
    console.error('Error fetching user companies:', error);
    return { success: false, error: error?.message };
  }
};

// Get company details with full information
export const getCompanyDetails = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('companies')?.select(`
        *,
        primary_owner:user_profiles!companies_primary_owner_id_fkey (
          id,
          full_name,
          email,
          username
        )
      `)?.eq('id', companyId)?.single();

    if (error) throw error;

    // Get company stats
    const [usersCount, jobsCount] = await Promise.all([
      getCompanyUsersCount(companyId),
      getCompanyJobsCount(companyId)
    ]);

    return {
      success: true,
      data: {
        ...data,
        primary_owner: data?.primary_owner || null,
        stats: {
          totalUsers: usersCount,
          totalJobs: jobsCount
        }
      }
    };
  } catch (error) {
    console.error('Error fetching company details:', error);
    return { success: false, error: error?.message };
  }
};

// Get company users count
export const getCompanyUsersCount = async (companyId) => {
  try {
    const { count, error } = await supabase?.from('company_users')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId)?.eq('is_active', true);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error getting company users count:', error);
    return 0;
  }
};

// Get company jobs count
export const getCompanyJobsCount = async (companyId) => {
  try {
    const { count, error } = await supabase?.from('jobs')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error getting company jobs count:', error);
    return 0;
  }
};

// Get companies user has ownership in (for merge simulation)
export const getUserOwnedCompanies = async (userId) => {
  try {
    const { data, error } = await supabase?.from('company_users')?.select(`
        id,
        role,
        company:companies (
          id,
          company_name,
          company_tag,
          status,
          created_at
        )
      `)?.eq('user_id', userId)?.in('role', ['primary_owner', 'owner'])?.eq('is_active', true);

    if (error) throw error;

    return {
      success: true,
      data: data?.map(item => ({
        membershipId: item?.id,
        role: item?.role,
        ...item?.company
      })) || []
    };
  } catch (error) {
    console.error('Error fetching owned companies:', error);
    return { success: false, error: error?.message };
  }
};

// Simulate company merge analysis (frontend only)
export const simulateCompanyMerge = async (companyAId, companyBId) => {
  try {
    // Get both companies' data
    const [companyA, companyB] = await Promise.all([
      getCompanyDetails(companyAId),
      getCompanyDetails(companyBId)
    ]);

    if (!companyA?.success || !companyB?.success) {
      throw new Error('Failed to fetch company details');
    }

    // Get users from both companies
    const [usersA, usersB] = await Promise.all([
      getCompanyUsers(companyAId),
      getCompanyUsers(companyBId)
    ]);

    // Get jobs from both companies
    const [jobsA, jobsB] = await Promise.all([
      getCompanyJobs(companyAId),
      getCompanyJobs(companyBId)
    ]);

    // Find conflicts
    const userConflicts = findUserConflicts(usersA?.data || [], usersB?.data || []);
    const duplicateJobs = findDuplicateJobs(jobsA?.data || [], jobsB?.data || []);

    return {
      success: true,
      data: {
        companyA: companyA?.data,
        companyB: companyB?.data,
        conflicts: {
          users: userConflicts,
          jobs: duplicateJobs,
          totalConflicts: userConflicts?.length + duplicateJobs?.length
        },
        mergedPreview: {
          name: `${companyA?.data?.company_name} + ${companyB?.data?.company_name}`,
          totalUsers: (usersA?.data?.length || 0) + (usersB?.data?.length || 0) - userConflicts?.length,
          totalJobs: (jobsA?.data?.length || 0) + (jobsB?.data?.length || 0)
        }
      }
    };
  } catch (error) {
    console.error('Error simulating company merge:', error);
    return { success: false, error: error?.message };
  }
};

// Get company users (exported for company details view)
export const getCompanyUsers = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('company_users')?.select(`
        id,
        role,
        user_id,
        company_alias,
        is_active,
        user:user_profiles (
          id,
          full_name,
          email,
          username
        )
      `)?.eq('company_id', companyId);

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, error: error?.message, data: [] };
  }
};

// Get company jobs (exported for company details view)
export const getCompanyJobs = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('jobs')?.select('id, job_name, job_type, status, created_at')?.eq('company_id', companyId)?.limit(100);

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    return { success: false, error: error?.message, data: [] };
  }
};

// Helper: Find user conflicts
const findUserConflicts = (usersA, usersB) => {
  const conflicts = [];
  const emailMapA = new Map(usersA?.map(u => [u?.user?.email, u]) || []);

  usersB?.forEach(userB => {
    const email = userB?.user?.email;
    if (emailMapA?.has(email)) {
      conflicts?.push({
        email,
        userA: emailMapA?.get(email),
        userB: userB,
        conflictType: 'duplicate_user'
      });
    }
  });

  return conflicts;
};

// Helper: Find duplicate jobs
const findDuplicateJobs = (jobsA, jobsB) => {
  const duplicates = [];
  const jobNamesA = new Set(jobsA?.map(j => j?.job_name?.toLowerCase()?.trim()) || []);

  jobsB?.forEach(jobB => {
    const jobName = jobB?.job_name?.toLowerCase()?.trim();
    if (jobNamesA?.has(jobName)) {
      duplicates?.push({
        jobName: jobB?.job_name,
        conflictType: 'duplicate_job_name'
      });
    }
  });

  return duplicates;
};

// Create new company with primary owner (API-First Approach)
export const createCompany = async (companyData) => {
  try {
    const { companyName, companyTag, ownerName, ownerEmail, ownerPassword } = companyData;

    // Validate password
    if (!ownerPassword || ownerPassword.length < 8) {
      throw new Error('Password must be at least 8 characters');
    }

    console.log('Creating company with owner password provided');

    // Call Edge Function which handles User Creation (API) + Company Creation (SQL)
    const { data, error } = await supabase.functions.invoke('create-company-owner', {
      body: {
        companyName,
        companyTag,
        ownerName,
        ownerEmail,
        tempPassword: ownerPassword
      }
    });

    if (error) {
      const errorMessage = await extractEdgeFunctionError(error, 'Failed to create company');
      throw new Error(errorMessage);
    }
    if (!data?.success) throw new Error(data?.error || 'Failed to create company');

    return {
      success: true,
      data: {
        company: {
          id: data.data.company_id,
          companyName: companyName,
          companyTag: companyTag
        },
        primaryOwner: {
          id: data.data.owner_id,
          email: ownerEmail,
          fullName: ownerName
        }
      },
      message: 'Company created successfully. Owner can now log in with the provided credentials.'
    };
  } catch (error) {
    console.error('Error creating company:', error);
    return {
      success: false,
      error: error?.message || 'Failed to create company'
    };
  }
};

// Transfer company ownership to a new primary owner
export const transferCompanyOwnership = async (companyId, newOwnerId) => {
  try {
    // 1. Get current primary owner
    const { data: currentOwner, error: currentError } = await supabase
      ?.from('company_users')
      ?.select('id, user_id')
      ?.eq('company_id', companyId)
      ?.eq('role', 'primary_owner')
      ?.single();

    if (currentError && currentError.code !== 'PGRST116') throw currentError;

    // 2. Demote current primary owner to regular owner
    if (currentOwner) {
      const { error: demoteError } = await supabase
        ?.from('company_users')
        ?.update({ role: 'owner', updated_at: new Date().toISOString() })
        ?.eq('id', currentOwner.id);

      if (demoteError) throw demoteError;
    }

    // 3. Promote new owner to primary_owner
    const { error: promoteError } = await supabase
      ?.from('company_users')
      ?.update({ role: 'primary_owner', updated_at: new Date().toISOString() })
      ?.eq('company_id', companyId)
      ?.eq('user_id', newOwnerId);

    if (promoteError) throw promoteError;

    // 4. Update companies table primary_owner_id
    const { error: companyError } = await supabase
      ?.from('companies')
      ?.update({ primary_owner_id: newOwnerId, updated_at: new Date().toISOString() })
      ?.eq('id', companyId);

    if (companyError) throw companyError;

    return { success: true, message: 'Ownership transferred successfully' };
  } catch (error) {
    console.error('Error transferring ownership:', error);
    return { success: false, error: error?.message || 'Failed to transfer ownership' };
  }
};

// Delete a company and all associated data
export const deleteCompany = async (companyId) => {
  try {
    // Note: This requires proper cascade delete setup in the database
    // or manual deletion of related records first

    // 1. Delete company_users records
    const { error: usersError } = await supabase
      ?.from('company_users')
      ?.delete()
      ?.eq('company_id', companyId);

    if (usersError) throw usersError;

    // 2. Delete jobs associated with company
    const { error: jobsError } = await supabase
      ?.from('jobs')
      ?.delete()
      ?.eq('company_id', companyId);

    if (jobsError) throw jobsError;

    // 3. Delete the company itself
    const { error: companyError } = await supabase
      ?.from('companies')
      ?.delete()
      ?.eq('id', companyId);

    if (companyError) throw companyError;

    return { success: true, message: 'Company deleted successfully' };
  } catch (error) {
    console.error('Error deleting company:', error);
    return { success: false, error: error?.message || 'Failed to delete company' };
  }
};

// Check if email exists in system and/or company
export const checkUserByEmail = async (email, companyId) => {
  try {
    // 1. Check user_profiles for email
    const { data: profile, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('id, email, full_name')
      ?.eq('email', email?.toLowerCase()?.trim())
      ?.single();

    if (profileError && profileError.code !== 'PGRST116') {
      throw profileError;
    }

    if (!profile) {
      return { success: true, exists: false, inCompany: false };
    }

    // 2. Check if user is in this company
    const { data: membership, error: membershipError } = await supabase
      ?.from('company_users')
      ?.select('id, role')
      ?.eq('company_id', companyId)
      ?.eq('user_id', profile.id)
      ?.eq('is_active', true)
      ?.single();

    if (membershipError && membershipError.code !== 'PGRST116') {
      throw membershipError;
    }

    return {
      success: true,
      exists: true,
      inCompany: !!membership,
      userId: profile.id,
      userName: profile.full_name,
      userEmail: profile.email,
      currentRole: membership?.role
    };
  } catch (error) {
    console.error('Error checking user by email:', error);
    return { success: false, error: error?.message };
  }
};

// Transfer ownership to existing Mervo user (not currently in this company)
export const transferOwnershipToExistingUser = async (companyId, userId, companyTag) => {
  try {
    // 1. Get current primary owner
    const { data: currentOwner, error: currentError } = await supabase
      ?.from('company_users')
      ?.select('id, user_id')
      ?.eq('company_id', companyId)
      ?.eq('role', 'primary_owner')
      ?.single();

    if (currentError && currentError.code !== 'PGRST116') throw currentError;

    // 2. Demote current primary owner to regular owner
    if (currentOwner) {
      const { error: demoteError } = await supabase
        ?.from('company_users')
        ?.update({ role: 'owner', updated_at: new Date().toISOString() })
        ?.eq('id', currentOwner.id);

      if (demoteError) throw demoteError;
    }

    // 3. Get user's email for alias
    const { data: userProfile } = await supabase
      ?.from('user_profiles')
      ?.select('email')
      ?.eq('id', userId)
      ?.single();

    const username = userProfile?.email?.split('@')?.[0] || 'user';

    // 4. Add new user to company as primary_owner
    const { error: insertError } = await supabase
      ?.from('company_users')
      ?.insert({
        company_id: companyId,
        user_id: userId,
        role: 'primary_owner',
        company_alias: `${username}@${companyTag?.toLowerCase()}`,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

    if (insertError) throw insertError;

    // 5. Update companies table primary_owner_id
    const { error: companyError } = await supabase
      ?.from('companies')
      ?.update({ primary_owner_id: userId, updated_at: new Date().toISOString() })
      ?.eq('id', companyId);

    if (companyError) throw companyError;

    return { success: true, message: 'Ownership transferred successfully' };
  } catch (error) {
    console.error('Error transferring ownership to existing user:', error);
    return { success: false, error: error?.message || 'Failed to transfer ownership' };
  }
};

// Transfer ownership to new user (creates account)
export const transferOwnershipToNewUser = async (companyId, companyTag, email, name, password) => {
  try {
    // Use existing edge function pattern for creating user
    const { data, error } = await supabase.functions.invoke('create-company-owner', {
      body: {
        companyName: null, // Not creating new company
        companyTag: companyTag,
        ownerName: name,
        ownerEmail: email,
        tempPassword: password,
        existingCompanyId: companyId, // Signal this is a transfer
        isTransfer: true
      }
    });

    if (error) {
      const errorMessage = await extractEdgeFunctionError(error, 'Failed to create new owner');
      throw new Error(errorMessage);
    }
    if (!data?.success) throw new Error(data?.error || 'Failed to create new owner');

    // If edge function doesn't handle transfer, do it here
    if (data?.data?.owner_id) {
      // Demote current primary owner
      const { data: currentOwner } = await supabase
        ?.from('company_users')
        ?.select('id')
        ?.eq('company_id', companyId)
        ?.eq('role', 'primary_owner')
        ?.single();

      if (currentOwner) {
        await supabase
          ?.from('company_users')
          ?.update({ role: 'owner', updated_at: new Date().toISOString() })
          ?.eq('id', currentOwner.id);
      }

      // Update companies table
      await supabase
        ?.from('companies')
        ?.update({ primary_owner_id: data.data.owner_id, updated_at: new Date().toISOString() })
        ?.eq('id', companyId);
    }

    return {
      success: true,
      message: 'New owner created and ownership transferred successfully',
      data: data?.data
    };
  } catch (error) {
    console.error('Error transferring ownership to new user:', error);
    return { success: false, error: error?.message || 'Failed to transfer ownership' };
  }
};

// ============================================
// COMPANY DELETION WORKFLOW
// ============================================

// Get comprehensive deletion preview with counts of all affected data
export const getCompanyDeletionPreview = async (companyId) => {
  try {
    // Get company details first
    const { data: company, error: companyError } = await supabase
      ?.from('companies')
      ?.select('id, company_name, company_tag, status, created_at, deleted_at, deletion_scheduled_at')
      ?.eq('id', companyId)
      ?.single();

    if (companyError) throw companyError;

    // Fetch counts for all related tables in parallel
    const [
      usersResult,
      jobsResult,
      jobInstancesResult,
      timesheetsResult,
      serviceReceiversResult,
      partnerAgenciesResult,
      templatesResult,
      permissionOverridesResult,
      notificationsResult,
      overtimeRequestsResult
    ] = await Promise.all([
      supabase?.from('company_users')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('jobs')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('job_instances')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('timesheets')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('service_receivers')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('partner_agencies')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('job_templates')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('user_permission_overrides')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('notifications')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
      supabase?.from('overtime_requests')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId)
    ]);

    // Get count of active jobs
    const { count: activeJobsCount } = await supabase
      ?.from('jobs')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('company_id', companyId)
      ?.eq('status', 'active');

    // Get affected users with details
    const { data: users } = await supabase
      ?.from('company_users')
      ?.select(`
        user_id,
        role,
        company_alias,
        user:user_profiles (
          id,
          full_name,
          email
        )
      `)
      ?.eq('company_id', companyId)
      ?.eq('is_active', true);

    // Generate warnings
    const warnings = [];
    if (activeJobsCount > 0) {
      warnings.push(`${activeJobsCount} active job${activeJobsCount > 1 ? 's' : ''} will be cancelled`);
    }
    if (usersResult?.count > 0) {
      warnings.push(`${usersResult.count} user${usersResult.count > 1 ? 's' : ''} will lose access to this company`);
    }
    if (timesheetsResult?.count > 0) {
      warnings.push(`${timesheetsResult.count} timesheet record${timesheetsResult.count > 1 ? 's' : ''} will be deleted`);
    }

    return {
      success: true,
      data: {
        company: {
          id: company?.id,
          name: company?.company_name,
          tag: company?.company_tag,
          status: company?.status,
          createdAt: company?.created_at,
          isDeleted: !!company?.deleted_at,
          deletionScheduledAt: company?.deletion_scheduled_at
        },
        counts: {
          users: usersResult?.count || 0,
          jobs: jobsResult?.count || 0,
          jobInstances: jobInstancesResult?.count || 0,
          timesheets: timesheetsResult?.count || 0,
          serviceReceivers: serviceReceiversResult?.count || 0,
          partnerAgencies: partnerAgenciesResult?.count || 0,
          templates: templatesResult?.count || 0,
          permissionOverrides: permissionOverridesResult?.count || 0,
          notifications: notificationsResult?.count || 0,
          overtimeRequests: overtimeRequestsResult?.count || 0
        },
        users: users?.map(u => ({
          id: u?.user_id,
          name: u?.user?.full_name,
          email: u?.user?.email,
          role: u?.role,
          companyAlias: u?.company_alias,
          willLose: ['Company access', 'Company alias', 'Permission overrides', 'Notification preferences']
        })) || [],
        warnings,
        activeJobsCount: activeJobsCount || 0
      }
    };
  } catch (error) {
    console.error('Error getting deletion preview:', error);
    return { success: false, error: error?.message || 'Failed to get deletion preview' };
  }
};

// Soft delete company (archive with 30-day grace period)
export const softDeleteCompany = async (companyId, reason, deletedByUserId) => {
  try {
    // Calculate scheduled permanent deletion date (30 days from now)
    const deletionScheduledAt = new Date();
    deletionScheduledAt.setDate(deletionScheduledAt.getDate() + 30);

    // Update company with soft delete fields
    const { data, error } = await supabase
      ?.from('companies')
      ?.update({
        status: 'deleted',
        deleted_at: new Date().toISOString(),
        deleted_by: deletedByUserId,
        deletion_reason: reason,
        deletion_scheduled_at: deletionScheduledAt.toISOString(),
        updated_at: new Date().toISOString()
      })
      ?.eq('id', companyId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      success: true,
      message: 'Company archived successfully. It can be restored within 30 days.',
      data: {
        companyId: data?.id,
        deletedAt: data?.deleted_at,
        deletionScheduledAt: data?.deletion_scheduled_at,
        canRestoreUntil: deletionScheduledAt.toISOString()
      }
    };
  } catch (error) {
    console.error('Error soft deleting company:', error);
    return { success: false, error: error?.message || 'Failed to archive company' };
  }
};

// Restore a soft-deleted company
export const restoreCompany = async (companyId) => {
  try {
    // Verify company is currently soft-deleted
    const { data: company, error: checkError } = await supabase
      ?.from('companies')
      ?.select('id, deleted_at, company_name')
      ?.eq('id', companyId)
      ?.single();

    if (checkError) throw checkError;
    if (!company?.deleted_at) {
      throw new Error('Company is not archived');
    }

    // Clear soft delete fields and restore to active
    const { error } = await supabase
      ?.from('companies')
      ?.update({
        status: 'active',
        deleted_at: null,
        deleted_by: null,
        deletion_reason: null,
        deletion_scheduled_at: null,
        updated_at: new Date().toISOString()
      })
      ?.eq('id', companyId);

    if (error) throw error;

    return {
      success: true,
      message: `Company "${company.company_name}" has been restored successfully.`
    };
  } catch (error) {
    console.error('Error restoring company:', error);
    return { success: false, error: error?.message || 'Failed to restore company' };
  }
};

/**
 * Update company status (suspend or reactivate)
 * @param {string} companyId - Company ID
 * @param {string} newStatus - New status ('active' or 'suspended')
 * @param {string} reason - Reason for status change
 * @param {string} updatedByUserId - User ID making the change
 * @returns {Promise<Object>} Result object
 */
export const updateCompanyStatus = async (companyId, newStatus, reason, updatedByUserId) => {
  try {
    // Validate status
    if (!['active', 'suspended'].includes(newStatus)) {
      throw new Error('Invalid status. Must be "active" or "suspended"');
    }

    // Get current company status
    const { data: company, error: fetchError } = await supabase
      .from('companies')
      .select('id, company_name, status')
      .eq('id', companyId)
      .single();

    if (fetchError) throw fetchError;
    if (!company) throw new Error('Company not found');

    // Check if status is actually changing
    if (company.status === newStatus) {
      return {
        success: true,
        message: `Company is already ${newStatus}`,
        noChange: true
      };
    }

    // Update company status
    const { error: updateError } = await supabase
      .from('companies')
      .update({
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', companyId);

    if (updateError) throw updateError;

    // Log the action to audit_logs
    const { error: auditError } = await supabase
      .from('audit_logs')
      .insert({
        user_id: updatedByUserId,
        company_id: companyId,
        action: newStatus === 'suspended' ? 'company_suspended' : 'company_reactivated',
        entity_type: 'company',
        entity_id: companyId,
        details: {
          reason,
          previous_status: company.status,
          new_status: newStatus,
          company_name: company.company_name
        },
        created_at: new Date().toISOString()
      });

    if (auditError) {
      console.error('Failed to log audit entry:', auditError);
      // Don't fail the whole operation if audit logging fails
    }

    return {
      success: true,
      message: `Company ${newStatus === 'suspended' ? 'suspended' : 'reactivated'} successfully`,
      data: {
        companyId,
        companyName: company.company_name,
        previousStatus: company.status,
        newStatus,
        reason,
        updatedAt: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('Error updating company status:', error);
    return {
      success: false,
      error: error?.message || 'Failed to update company status'
    };
  }
};

// Hard delete company (permanent - only after 30-day grace period)
export const hardDeleteCompany = async (companyId) => {
  try {
    // Verify company is soft-deleted and grace period has passed
    const { data: company, error: checkError } = await supabase
      ?.from('companies')
      ?.select('id, deleted_at, deletion_scheduled_at, company_name')
      ?.eq('id', companyId)
      ?.single();

    if (checkError) throw checkError;

    if (!company?.deleted_at) {
      throw new Error('Company must be archived before permanent deletion');
    }

    const now = new Date();
    const scheduledDate = new Date(company.deletion_scheduled_at);
    if (now < scheduledDate) {
      const daysRemaining = Math.ceil((scheduledDate - now) / (1000 * 60 * 60 * 24));
      throw new Error(`30-day grace period has not elapsed. ${daysRemaining} day(s) remaining.`);
    }

    // Delete in correct order to respect foreign key constraints
    // Note: Many of these will cascade automatically, but being explicit for safety

    // 1. Delete job-related data (cascades from jobs)
    const { error: jobsError } = await supabase
      ?.from('jobs')
      ?.delete()
      ?.eq('company_id', companyId);
    if (jobsError) throw jobsError;

    // 2. Delete overtime requests
    const { error: overtimeError } = await supabase
      ?.from('overtime_requests')
      ?.delete()
      ?.eq('company_id', companyId);
    if (overtimeError) throw overtimeError;

    // 3. Delete permission overrides
    const { error: permError } = await supabase
      ?.from('user_permission_overrides')
      ?.delete()
      ?.eq('company_id', companyId);
    if (permError) throw permError;

    // 4. Delete notifications
    const { error: notifError } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('company_id', companyId);
    if (notifError) throw notifError;

    // 5. Delete notification preferences
    const { error: notifPrefError } = await supabase
      ?.from('notification_preferences')
      ?.delete()
      ?.eq('company_id', companyId);
    if (notifPrefError) throw notifPrefError;

    // 6. Delete pending invites
    const { error: invitesError } = await supabase
      ?.from('pending_company_invites')
      ?.delete()
      ?.eq('company_id', companyId);
    if (invitesError) throw invitesError;

    // 7. Delete agency display preferences
    const { error: agencyPrefError } = await supabase
      ?.from('agency_display_preferences')
      ?.delete()
      ?.eq('company_id', companyId);
    if (agencyPrefError) throw agencyPrefError;

    // 8. Delete partner agencies
    const { error: agenciesError } = await supabase
      ?.from('partner_agencies')
      ?.delete()
      ?.eq('company_id', companyId);
    if (agenciesError) throw agenciesError;

    // 9. Delete service receivers
    const { error: receiversError } = await supabase
      ?.from('service_receivers')
      ?.delete()
      ?.eq('company_id', companyId);
    if (receiversError) throw receiversError;

    // 10. Delete job templates
    const { error: templatesError } = await supabase
      ?.from('job_templates')
      ?.delete()
      ?.eq('company_id', companyId);
    if (templatesError) throw templatesError;

    // 11. Delete job ID counters
    const { error: countersError } = await supabase
      ?.from('job_id_counters')
      ?.delete()
      ?.eq('company_id', companyId);
    if (countersError) throw countersError;

    // 12. Delete company settings
    const { error: settingsError } = await supabase
      ?.from('company_settings')
      ?.delete()
      ?.eq('company_id', companyId);
    if (settingsError) throw settingsError;

    // 13. Delete company users
    const { error: usersError } = await supabase
      ?.from('company_users')
      ?.delete()
      ?.eq('company_id', companyId);
    if (usersError) throw usersError;

    // 14. Finally delete the company
    const { error: companyError } = await supabase
      ?.from('companies')
      ?.delete()
      ?.eq('id', companyId);
    if (companyError) throw companyError;

    return {
      success: true,
      message: `Company "${company.company_name}" and all associated data have been permanently deleted.`
    };
  } catch (error) {
    console.error('Error hard deleting company:', error);
    return { success: false, error: error?.message || 'Failed to permanently delete company' };
  }
};

// Helper function to convert array of objects to CSV string
const arrayToCSV = (data, columns) => {
  if (!data || data.length === 0) return '';

  // Use provided columns or extract from first object
  const headers = columns || Object.keys(data[0]);

  // Escape CSV values (handle commas, quotes, newlines)
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Build CSV string
  const headerRow = headers.map(escapeCSV).join(',');
  const dataRows = data.map(row =>
    headers.map(header => escapeCSV(row[header])).join(',')
  ).join('\n');

  return `${headerRow}\n${dataRows}`;
};

// Export company data before deletion (supports JSON, CSV, and Excel formats)
// Exports ALL company-related data from all tables
export const exportCompanyData = async (companyId, format = 'json') => {
  try {
    // Get company details
    const { data: company, error: companyError } = await supabase
      ?.from('companies')
      ?.select('*')
      ?.eq('id', companyId)
      ?.single();
    if (companyError) throw companyError;

    // Fetch ALL related data from ALL tables in parallel
    const [
      usersResult,
      jobsResult,
      jobInstancesResult,
      timesheetsResult,
      jobPhotosResult,
      serviceReceiversResult,
      partnerAgenciesResult,
      agencyDisplayPrefsResult,
      templatesResult,
      overtimeRequestsResult,
      permissionOverridesResult,
      notificationsResult,
      notificationPrefsResult,
      pendingInvitesResult,
      companySettingsResult,
      jobIdCountersResult,
      auditLogsResult
    ] = await Promise.all([
      supabase?.from('company_users')?.select(`
        id, user_id, role, company_alias, is_active, account_type, created_at, updated_at,
        user:user_profiles (id, full_name, email, username, phone_number)
      `)?.eq('company_id', companyId),
      supabase?.from('jobs')?.select('*')?.eq('company_id', companyId),
      supabase?.from('job_instances')?.select('*')?.eq('company_id', companyId),
      supabase?.from('timesheets')?.select('*')?.eq('company_id', companyId),
      supabase?.from('job_photos')?.select('*')?.eq('job_instance_id', companyId), // Will filter by job_instance later
      supabase?.from('service_receivers')?.select('*')?.eq('company_id', companyId),
      supabase?.from('partner_agencies')?.select('*')?.eq('company_id', companyId),
      supabase?.from('agency_display_preferences')?.select('*')?.eq('company_id', companyId),
      supabase?.from('job_templates')?.select('*')?.eq('company_id', companyId),
      supabase?.from('overtime_requests')?.select('*')?.eq('company_id', companyId),
      supabase?.from('user_permission_overrides')?.select('*')?.eq('company_id', companyId),
      supabase?.from('notifications')?.select('*')?.eq('company_id', companyId),
      supabase?.from('notification_preferences')?.select('*')?.eq('company_id', companyId),
      supabase?.from('pending_company_invites')?.select('*')?.eq('company_id', companyId),
      supabase?.from('company_settings')?.select('*')?.eq('company_id', companyId),
      supabase?.from('job_id_counters')?.select('*')?.eq('company_id', companyId),
      supabase?.from('audit_logs')?.select('*')?.eq('company_id', companyId)?.order('created_at', { ascending: false })?.limit(5000)
    ]);

    // Get job photos via job instances (need to query differently since job_photos links to job_instances)
    const jobInstanceIds = (jobInstancesResult?.data || []).map(ji => ji.id);
    let jobPhotos = [];
    if (jobInstanceIds.length > 0) {
      const { data: photos } = await supabase
        ?.from('job_photos')
        ?.select('*')
        ?.in('job_instance_id', jobInstanceIds);
      jobPhotos = photos || [];
    }

    const dateStr = new Date().toISOString().split('T')[0];

    // Flatten users data (nested user object) - used by both CSV and Excel formats
    const flattenedUsers = (usersResult?.data || []).map(u => ({
      id: u?.id,
      user_id: u?.user_id,
      full_name: u?.user?.full_name,
      email: u?.user?.email,
      username: u?.user?.username,
      phone_number: u?.user?.phone_number,
      role: u?.role,
      account_type: u?.account_type,
      company_alias: u?.company_alias,
      is_active: u?.is_active,
      created_at: u?.created_at,
      updated_at: u?.updated_at
    }));

    // EXCEL format - single file with multiple tabs/worksheets
    if (format === 'excel') {
      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Helper to calculate column widths based on content
      const getColumnWidths = (data) => {
        if (!data || data.length === 0) return [{ wch: 20 }];

        const headers = Object.keys(data[0]);
        return headers.map(header => {
          // Get max length of header and all values in this column
          let maxLength = header.length;
          data.forEach(row => {
            const value = row[header];
            if (value !== null && value !== undefined) {
              const strValue = String(value);
              // For long text, cap at reasonable width
              maxLength = Math.max(maxLength, Math.min(strValue.length, 50));
            }
          });
          // Add some padding and set minimum width
          return { wch: Math.max(maxLength + 2, 10) };
        });
      };

      // Helper to add a sheet with auto-fit columns
      const addSheet = (data, sheetName) => {
        const sheetData = data?.length > 0 ? data : [{ 'No Data': 'No records found' }];
        const ws = XLSX.utils.json_to_sheet(sheetData);

        // Set column widths based on content
        ws['!cols'] = getColumnWidths(sheetData);

        XLSX.utils.book_append_sheet(workbook, ws, sheetName);
      };

      // Add all sheets with data
      addSheet(flattenedUsers, 'Users');
      addSheet(jobsResult?.data || [], 'Jobs');
      addSheet(jobInstancesResult?.data || [], 'Job Instances');
      addSheet(timesheetsResult?.data || [], 'Timesheets');
      addSheet(jobPhotos, 'Job Photos');
      addSheet(serviceReceiversResult?.data || [], 'Service Receivers');
      addSheet(partnerAgenciesResult?.data || [], 'Partner Agencies');
      addSheet(agencyDisplayPrefsResult?.data || [], 'Agency Display Prefs');
      addSheet(templatesResult?.data || [], 'Job Templates');
      addSheet(companySettingsResult?.data || [], 'Company Settings');
      addSheet(jobIdCountersResult?.data || [], 'Job ID Counters');
      addSheet(overtimeRequestsResult?.data || [], 'Overtime Requests');
      addSheet(permissionOverridesResult?.data || [], 'Permission Overrides');
      addSheet(notificationsResult?.data || [], 'Notifications');
      addSheet(notificationPrefsResult?.data || [], 'Notification Prefs');
      addSheet(pendingInvitesResult?.data || [], 'Pending Invites');
      addSheet(auditLogsResult?.data || [], 'Audit Logs');

      // Add summary sheet at the beginning
      const summaryData = [
        { Category: 'Company Name', Value: company?.company_name },
        { Category: 'Company Tag', Value: company?.company_tag },
        { Category: 'Status', Value: company?.status },
        { Category: 'Export Date', Value: new Date().toISOString() },
        { Category: '', Value: '' },
        { Category: 'Data Summary', Value: 'Record Count' },
        { Category: 'Users', Value: flattenedUsers.length },
        { Category: 'Jobs', Value: jobsResult?.data?.length || 0 },
        { Category: 'Job Instances', Value: jobInstancesResult?.data?.length || 0 },
        { Category: 'Timesheets', Value: timesheetsResult?.data?.length || 0 },
        { Category: 'Job Photos', Value: jobPhotos.length },
        { Category: 'Service Receivers', Value: serviceReceiversResult?.data?.length || 0 },
        { Category: 'Partner Agencies', Value: partnerAgenciesResult?.data?.length || 0 },
        { Category: 'Job Templates', Value: templatesResult?.data?.length || 0 },
        { Category: 'Overtime Requests', Value: overtimeRequestsResult?.data?.length || 0 },
        { Category: 'Permission Overrides', Value: permissionOverridesResult?.data?.length || 0 },
        { Category: 'Notifications', Value: notificationsResult?.data?.length || 0 },
        { Category: 'Pending Invites', Value: pendingInvitesResult?.data?.length || 0 },
        { Category: 'Audit Logs', Value: auditLogsResult?.data?.length || 0 }
      ];
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      // Set summary column widths
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 40 }];
      // Insert summary at beginning of workbook
      workbook.SheetNames.unshift('Summary');
      workbook.Sheets['Summary'] = summaryWs;

      // Generate Excel file as array buffer
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      return {
        success: true,
        format: 'excel',
        buffer: excelBuffer,
        filename: `company_export_${company?.company_tag}_${dateStr}.xlsx`,
        companyTag: company?.company_tag
      };
    }

    // CSV format - return multiple CSV files data
    if (format === 'csv') {
      const csvFiles = {
        // Core company data
        [`01_users_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(flattenedUsers, [
          'id', 'user_id', 'full_name', 'email', 'username', 'phone_number', 'role', 'account_type', 'company_alias', 'is_active', 'created_at', 'updated_at'
        ]),

        // Jobs & work data
        [`02_jobs_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(jobsResult?.data || [], [
          'id', 'job_name', 'job_id_display', 'description', 'job_type', 'status', 'priority', 'payment_type', 'payment_amount', 'location_address', 'location_lat', 'location_lng', 'service_receiver_id', 'partner_agency_id', 'created_by', 'created_at', 'updated_at'
        ]),
        [`03_job_instances_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(jobInstancesResult?.data || [], [
          'id', 'job_id', 'assigned_contractor', 'scheduled_date', 'scheduled_time', 'status', 'clock_in_time', 'clock_in_location', 'clock_out_time', 'clock_out_location', 'work_notes', 'created_at', 'updated_at'
        ]),
        [`04_timesheets_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(timesheetsResult?.data || [], [
          'id', 'job_instance_id', 'contractor_id', 'clock_in_time', 'clock_out_time', 'total_hours', 'regular_hours', 'overtime_hours', 'amount_earned', 'overtime_amount', 'status', 'approved_by', 'approved_at', 'created_at', 'updated_at'
        ]),
        [`05_job_photos_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(jobPhotos, [
          'id', 'job_instance_id', 'uploaded_by', 'photo_type', 'storage_path', 'caption', 'uploaded_at'
        ]),

        // Clients & agencies
        [`06_service_receivers_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(serviceReceiversResult?.data || [], [
          'id', 'receiver_name', 'receiver_type', 'contact_person', 'contact_email', 'contact_phone', 'address', 'notes', 'is_active', 'created_at', 'updated_at'
        ]),
        [`07_partner_agencies_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(partnerAgenciesResult?.data || [], [
          'id', 'agency_name', 'agency_code', 'contact_person', 'contact_email', 'contact_phone', 'address', 'notes', 'is_active', 'created_at', 'updated_at'
        ]),
        [`08_agency_display_prefs_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(agencyDisplayPrefsResult?.data || [], [
          'id', 'user_id', 'agency_id', 'is_visible', 'created_at', 'updated_at'
        ]),

        // Templates & settings
        [`09_job_templates_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(templatesResult?.data || [], [
          'id', 'template_name', 'description', 'job_type', 'payment_type', 'payment_amount', 'location_address', 'created_by', 'created_at', 'updated_at'
        ]),
        [`10_company_settings_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(companySettingsResult?.data || [], [
          'id', 'timezone', 'date_format', 'time_format', 'currency', 'overtime_enabled', 'overtime_threshold', 'overtime_multiplier', 'created_at', 'updated_at'
        ]),
        [`11_job_id_counters_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(jobIdCountersResult?.data || [], [
          'id', 'year_month', 'current_count', 'created_at', 'updated_at'
        ]),

        // Overtime & payments
        [`12_overtime_requests_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(overtimeRequestsResult?.data || [], [
          'id', 'job_instance_id', 'contractor_id', 'requested_hours', 'reason', 'status', 'approved_by', 'approved_at', 'created_at', 'updated_at'
        ]),

        // Permissions & notifications
        [`13_permission_overrides_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(permissionOverridesResult?.data || [], [
          'id', 'user_id', 'permission_id', 'is_granted', 'created_at', 'updated_at'
        ]),
        [`14_notifications_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(notificationsResult?.data || [], [
          'id', 'user_id', 'type', 'title', 'message', 'reference_type', 'reference_id', 'is_read', 'created_at'
        ]),
        [`15_notification_prefs_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(notificationPrefsResult?.data || [], [
          'id', 'user_id', 'notification_type', 'email_enabled', 'push_enabled', 'in_app_enabled', 'created_at', 'updated_at'
        ]),

        // Invites & audit
        [`16_pending_invites_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(pendingInvitesResult?.data || [], [
          'id', 'email', 'role', 'invited_by', 'status', 'expires_at', 'created_at'
        ]),
        [`17_audit_logs_${company?.company_tag}_${dateStr}.csv`]: arrayToCSV(auditLogsResult?.data || [], [
          'id', 'user_id', 'action', 'entity_type', 'entity_id', 'old_values', 'new_values', 'ip_address', 'user_agent', 'created_at'
        ])
      };

      return {
        success: true,
        format: 'csv',
        files: csvFiles,
        companyTag: company?.company_tag
      };
    }

    // JSON format (default) - includes ALL data
    const exportData = {
      exportedAt: new Date().toISOString(),
      exportVersion: '2.0',
      company: {
        id: company?.id,
        name: company?.company_name,
        tag: company?.company_tag,
        status: company?.status,
        createdAt: company?.created_at,
        updatedAt: company?.updated_at,
        primaryOwnerId: company?.primary_owner_id,
        deletedAt: company?.deleted_at,
        deletionReason: company?.deletion_reason
      },
      summary: {
        totalUsers: usersResult?.data?.length || 0,
        totalJobs: jobsResult?.data?.length || 0,
        totalJobInstances: jobInstancesResult?.data?.length || 0,
        totalTimesheets: timesheetsResult?.data?.length || 0,
        totalJobPhotos: jobPhotos?.length || 0,
        totalServiceReceivers: serviceReceiversResult?.data?.length || 0,
        totalPartnerAgencies: partnerAgenciesResult?.data?.length || 0,
        totalAgencyDisplayPrefs: agencyDisplayPrefsResult?.data?.length || 0,
        totalTemplates: templatesResult?.data?.length || 0,
        totalOvertimeRequests: overtimeRequestsResult?.data?.length || 0,
        totalPermissionOverrides: permissionOverridesResult?.data?.length || 0,
        totalNotifications: notificationsResult?.data?.length || 0,
        totalNotificationPrefs: notificationPrefsResult?.data?.length || 0,
        totalPendingInvites: pendingInvitesResult?.data?.length || 0,
        totalCompanySettings: companySettingsResult?.data?.length || 0,
        totalJobIdCounters: jobIdCountersResult?.data?.length || 0,
        totalAuditLogs: auditLogsResult?.data?.length || 0
      },
      data: {
        // Core company data
        users: usersResult?.data || [],

        // Jobs & work data
        jobs: jobsResult?.data || [],
        jobInstances: jobInstancesResult?.data || [],
        timesheets: timesheetsResult?.data || [],
        jobPhotos: jobPhotos,

        // Clients & agencies
        serviceReceivers: serviceReceiversResult?.data || [],
        partnerAgencies: partnerAgenciesResult?.data || [],
        agencyDisplayPreferences: agencyDisplayPrefsResult?.data || [],

        // Templates & settings
        jobTemplates: templatesResult?.data || [],
        companySettings: companySettingsResult?.data || [],
        jobIdCounters: jobIdCountersResult?.data || [],

        // Overtime & payments
        overtimeRequests: overtimeRequestsResult?.data || [],

        // Permissions & notifications
        permissionOverrides: permissionOverridesResult?.data || [],
        notifications: notificationsResult?.data || [],
        notificationPreferences: notificationPrefsResult?.data || [],

        // Invites & audit
        pendingInvites: pendingInvitesResult?.data || [],
        auditLogs: auditLogsResult?.data || []
      }
    };

    return {
      success: true,
      format: 'json',
      data: exportData,
      filename: `company_export_${company?.company_tag}_${dateStr}.json`
    };
  } catch (error) {
    console.error('Error exporting company data:', error);
    return { success: false, error: error?.message || 'Failed to export company data' };
  }
};

export default {
  getAllCompanies,
  getUserCompanies,
  getCompanyDetails,
  getCompanyUsers,
  getCompanyJobs,
  getUserOwnedCompanies,
  simulateCompanyMerge,
  createCompany,
  transferCompanyOwnership,
  deleteCompany,
  checkUserByEmail,
  transferOwnershipToExistingUser,
  transferOwnershipToNewUser,
  updateCompanyStatus,
  // Company Deletion Workflow
  getCompanyDeletionPreview,
  softDeleteCompany,
  restoreCompany,
  hardDeleteCompany,
  exportCompanyData
};